import { useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'

import { ToggleSetButton } from '@components/ToggleSetButton'
import type { Entity } from '@models/Entity'

import { PageSection } from '../../../theme/components/PageSection'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import type { Contactpersoon } from '../types/Contactpersoon'

import { ContactpersoonCard } from './ContactpersoonCard'
import { ContactpersoonDialog } from './ContactpersoonDialog'
import { ContactpersoonLog } from './ContactpersoonLog'

const cancelledFilter = (x) => x.status === 'cancelled'

const ICON_SIZE_EXPANDED = 120
const ICON_SIZE_COLLAPSED = 40

interface Props {
  contactpersonen: Contactpersoon[]
  entity: Entity
}

export const ContactpersonenCards = ({ contactpersonen, entity }: Props) => {
  const theme = useTheme()

  const [showLog, setShowLog] = useState(false)
  const [showCancelled, setShowCancelled] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const [type, setType] = useState<'edit' | 'create' | 'delete' | undefined>(
    undefined,
  )
  const [contactpersoon, setContactpersoon] = useState(contactpersonen[0])

  const handleClick = (
    type: 'edit' | 'create' | 'delete',
    contactpersoon?: Contactpersoon,
  ) => {
    if (contactpersoon) {
      setContactpersoon(contactpersoon)
    }

    setType(type)
  }

  return (
    <PageSection
      id="contactpersonen"
      heading="Contactpersonen"
      subheading="Voor project"
      actions={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <StyledIconButton
            title={expanded ? 'Gedeeltelijke weergave' : 'Volledige weergave'}
            onClick={() => setExpanded(!expanded)}
            icon={
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: `transform ${theme.transitions.duration.shorter}ms`,
                }}
              />
            }
          />

          <ToggleSetButton
            active={showCancelled}
            onClick={() => setShowCancelled(!showCancelled)}
            toggledSetSize={contactpersonen.filter(cancelledFilter).length}
            type="contactpersonen"
          />
        </Box>
      }
    >
      <Grid container spacing={4}>
        {contactpersonen
          .filter((x) => showCancelled || x.status !== 'cancelled')
          .map((contactpersoon) => (
            <Grid
              key={contactpersoon._id}
              size={{ xs: 12, md: 6, lg: 4, xl: 3 }}
              sx={{ display: 'flex' }}
            >
              <ContactpersoonCard
                contactpersoon={contactpersoon}
                onOpenLog={() => {
                  setContactpersoon(contactpersoon)
                  setShowLog(true)
                }}
                onEdit={() => handleClick('edit', contactpersoon)}
                onDelete={() => handleClick('delete', contactpersoon)}
                expanded={expanded}
              />
            </Grid>
          ))}

        <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }} sx={{ display: 'flex' }}>
          <Card
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              backgroundColor: 'action.hover',
            }}
          >
            <Tooltip title="Contactpersoon toevoegen">
              <CardActionArea
                onClick={() => handleClick('create')}
                sx={{ flexGrow: 1 }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <PersonAddAltIcon
                    sx={{
                      fontSize: expanded
                        ? ICON_SIZE_EXPANDED
                        : ICON_SIZE_COLLAPSED,
                      transition: `font-size ${theme.transitions.duration.standard}ms`,
                      opacity: 0.1,
                    }}
                  />
                </CardContent>
              </CardActionArea>
            </Tooltip>
          </Card>
        </Grid>
      </Grid>

      <ContactpersoonLog
        open={showLog}
        onClose={() => setShowLog(false)}
        contactpersoon={contactpersoon}
      />

      <ContactpersoonDialog
        entity={entity}
        type={type}
        onClose={() => setType(undefined)}
        contactpersoon={contactpersoon}
      />
    </PageSection>
  )
}
